import { useEffect, useState } from "react";
import { api, dispDT } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, RotateCcw } from "lucide-react";
import { PageLoader } from "@/components/PageLoader";

export default function AdminSimulasi() {
  const [settings, setSettings] = useState({});
  const [simNow, setSimNow] = useState("");
  const [tolerance, setTolerance] = useState("08:30");
  const [punctuality, setPunctuality] = useState("08:10");
  const [now, setNow] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () =>
    Promise.all([
      api.get("/settings").then((r) => {
        setSettings(r.data);
        if (r.data.sim_now) setSimNow(r.data.sim_now.replace(" ", "T").slice(0, 16));
        if (r.data.late_tolerance) setTolerance(r.data.late_tolerance);
        if (r.data.punctuality_threshold) setPunctuality(r.data.punctuality_threshold);
      }),
      api.get("/time/now").then((r) => setNow(r.data)),
    ]);
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const saveSim = async () => {
    const v = simNow.replace("T", " ") + ":00";
    await api.post("/settings", { key: "sim_now", value: v });
    toast.success("Simulasi aktif");
    refresh();
  };

  const clearSim = async () => {
    await api.post("/settings", { key: "sim_now", value: null });
    setSimNow("");
    toast.success("Kembali ke waktu real");
    refresh();
  };

  const saveTol = async () => {
    await api.post("/settings", { key: "late_tolerance", value: tolerance });
    toast.success("Toleransi disimpan");
  };

  const savePunctuality = async () => {
    if (!/^\d{2}:\d{2}$/.test(punctuality)) {
      toast.error("Format harus HH:MM");
      return;
    }
    await api.post("/settings", { key: "punctuality_threshold", value: punctuality });
    toast.success("Batas ketepatan waktu disimpan. Skor performa akan menyesuaikan.");
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-3xl mx-auto">
      <p className="overline">Setting</p>
      <h1 className="font-display text-3xl font-bold tracking-tight">Simulasi Waktu</h1>

      {loading ? <PageLoader label="Memuat..." /> : (<>
      <Card className="mt-6 p-6 border border-zinc-200 shadow-none">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-zinc-500" />
          <div>
            <p className="text-xs text-zinc-500">Waktu Sistem Saat Ini</p>
            <p className="font-display text-xl font-bold" data-testid="current-sim-time">{dispDT(now?.now)}</p>
            {now?.is_sim && <p className="text-xs text-amber-700 font-semibold">MODE SIMULASI</p>}
          </div>
        </div>
      </Card>

      <Card className="mt-4 p-6 border border-zinc-200 shadow-none">
        <p className="overline mb-3">Atur Waktu Simulasi</p>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Waktu (Y-m-d H:i)</Label>
            <Input type="datetime-local" value={simNow} onChange={(e) => setSimNow(e.target.value)} className="mt-1" data-testid="sim-input" />
          </div>
          <Button onClick={saveSim} className="bg-primary hover:bg-primary/90" data-testid="btn-save-sim">Aktifkan</Button>
          <Button onClick={clearSim} variant="outline" data-testid="btn-clear-sim"><RotateCcw className="w-4 h-4 mr-1" />Real</Button>
        </div>
      </Card>

      <Card className="mt-4 p-6 border border-zinc-200 shadow-none">
        <p className="overline mb-3">Toleransi Telat</p>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Batas Jam (HH:MM)</Label>
            <Input type="time" value={tolerance} onChange={(e) => setTolerance(e.target.value)} className="mt-1" data-testid="tol-input" />
          </div>
          <Button onClick={saveTol} className="bg-primary hover:bg-primary/90" data-testid="btn-save-tol">Simpan</Button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">Default 08:30. Telat dihitung sejak 08:00 jika masuk &gt; toleransi.</p>
      </Card>

      <Card className="mt-4 p-6 border border-zinc-200 shadow-none">
        <p className="overline mb-3">Batas Ketepatan Waktu (Skor Kedisiplinan)</p>
        <div className="flex gap-2 items-end">
          <div className="flex-1">
            <Label>Batas Jam (HH:MM)</Label>
            <Input type="time" value={punctuality} onChange={(e) => setPunctuality(e.target.value)} className="mt-1" data-testid="punct-input" />
          </div>
          <Button onClick={savePunctuality} className="bg-primary hover:bg-primary/90" data-testid="btn-save-punct">Simpan</Button>
        </div>
        <p className="text-xs text-zinc-500 mt-2">
          Default 08:10. Datang &le; jam ini dihitung &quot;tepat waktu&quot; untuk skor Kedisiplinan. Berbeda dari Toleransi Telat
          di atas (yang untuk potongan gaji). Perubahan akan me-recompute skor performa seluruh bulan.
        </p>
      </Card>
      </>)}
    </div>
  );
}
